import { DISTRICTS } from "../data/city";
import { GameState } from "../systems/GameState";
import { SaveManager } from "../core/SaveManager";
import { heroArt } from "./illustrations";
import { openProgressModal } from "./progressModal";

/**
 * PORTADA / pantalla de inicio (estilo landing de videojuego, al modo de
 * ProfesionalPoly pero con la identidad de EPI: inspector de seguridad / obra,
 * chapa metálica, cinta de peligro, tarjetas hexagonales). Se muestra a pantalla
 * completa sobre el juego; al pulsar "Empezar" se desvanece y desbloquea el audio.
 * Detecta partida guardada para ofrecer "Continuar".
 */

const HELMET = `<svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true"><path d="M4 16h16v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" fill="#f0c400"/><path d="M4 16c0-5 3-9 8-9s8 4 8 9" fill="none" stroke="#241d14" stroke-width="2"/><path d="M12 7V4" stroke="#241d14" stroke-width="2" stroke-linecap="round"/></svg>`;
const IC_SEARCH = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#171308" stroke-width="2.2"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M20 20l-4.7-4.7" stroke-linecap="round"/></svg>`;
const IC_CLIP = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#171308" stroke-width="2"><rect x="5" y="4" width="14" height="17" rx="2"/><path d="M9 4V3a3 3 0 0 1 6 0v1" fill="#171308"/><path d="M8.5 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const IC_HAZ = `<svg viewBox="0 0 24 24" width="22" height="22"><path d="M12 3l9 16H3z" fill="none" stroke="#171308" stroke-width="2.2" stroke-linejoin="round"/><rect x="11" y="9" width="2" height="5" fill="#171308"/><circle cx="12" cy="16.5" r="1.3" fill="#171308"/></svg>`;
const IC_PLAY = `<svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor"><path d="M7 5l12 7-12 7z"/></svg>`;
const IC_BOOK = `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 5a2 2 0 0 1 2-2h5v16H6a2 2 0 0 0-2 2zM20 5a2 2 0 0 0-2-2h-5v16h5a2 2 0 0 1 2 2z"/></svg>`;
const IC_LEAF = `<svg viewBox="0 0 24 24" width="15" height="15" fill="#171308"><path d="M5 19c0-8 6-13 15-13 0 9-5 15-13 15-1 0-2 0-2-2z"/><path d="M8 16c3-4 6-6 9-7" stroke="#e9d27a" stroke-width="1.4" fill="none"/></svg>`;
const IC_GRAD = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#9c8b6c" stroke-width="1.8"><path d="M12 4l10 5-10 5L2 9z" fill="#9c8b6c" stroke="none"/><path d="M6 11v5c0 1 3 2.5 6 2.5S18 17 18 16v-5"/></svg>`;
const IC_SHIELD = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#9c8b6c" stroke-width="1.8"><path d="M12 3l8 3v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z"/><path d="M8.5 12l2.4 2.4 4.6-5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const YEAR = new Date().getFullYear();
const CONTACT_EMAIL = "elsorrogutierrez@gmail.com";

const INFO: Record<string, { title: string; color: string; html: string }> = {
  contacto: {
    title: "CONTACTO",
    color: "#185fa5",
    html: `
      <p>¿Dudas, sugerencias, quieres usarlo <b>en el aula</b> o has encontrado un fallo? Escríbenos:</p>
      <p class="pt-info-mail"><a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a></p>
      <p>Las Aventuras de EPI forma parte del ecosistema educativo <b>Mi Ruta</b>, de orientación y competencias profesionales.</p>
      <p class="pt-info-small">Respondemos según disponibilidad. Para emergencias reales de seguridad y salud, contacta siempre con los servicios oficiales.</p>`,
  },
  aviso: {
    title: "AVISO LEGAL",
    color: "#8a5a10",
    html: `
      <p><b>Las Aventuras de EPI</b> es un juego educativo de sensibilización en <b>Prevención de Riesgos Laborales (PRL)</b>, parte del ecosistema <b>Mi Ruta</b>. Su finalidad es divulgativa y formativa.</p>
      <p><b>Contenido orientativo.</b> El juego ayuda a entender y practicar buenas prácticas de prevención, pero <b>no sustituye</b> la formación oficial en PRL, la evaluación de riesgos de cada puesto, ni el criterio de un técnico de prevención. Tampoco constituye asesoramiento jurídico. La normativa puede cambiar: ante un caso real, consulta a un profesional y las fuentes oficiales (INSST, BOE).</p>
      <p><b>Propiedad intelectual.</b> Los textos, ilustraciones y mecánicas son originales del proyecto. Las marcas o normas citadas pertenecen a sus titulares y se mencionan solo con fines educativos.</p>
      <p><b>Responsabilidad.</b> El contenido se ofrece "tal cual", sin garantías. El uso de la información es responsabilidad de quien la utiliza.</p>
      <p class="pt-info-small">Contacto: <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a></p>`,
  },
  privacidad: {
    title: "PRIVACIDAD",
    color: "#1e6b3f",
    html: `
      <p><b>No recogemos datos personales.</b> El juego funciona íntegramente en tu navegador; no hay registro, ni cuentas, ni formularios.</p>
      <p><b>Tu progreso se guarda solo en tu dispositivo</b>, en el almacenamiento local del navegador (<i>localStorage</i>). No se envía a ningún servidor. Puedes borrarlo con <b>"Empezar de cero"</b> o limpiando los datos del navegador.</p>
      <p>Para continuar en <b>otro ordenador</b> puedes generar un <b>código de progreso</b> ("Guardar / Continuar"): se crea en tu navegador y solo lo compartes tú; no se envía a ningún sitio.</p>
      <p><b>Sin cookies de seguimiento</b> ni analítica de terceros.</p>
      <p><b>Alojamiento.</b> La web se sirve a través de un proveedor de hosting (Netlify), que puede registrar datos técnicos de acceso (por ejemplo, la IP) de forma agregada y por seguridad, según su propia política.</p>
      <p><b>Menores.</b> Es apto para uso educativo y no solicita ningún dato personal.</p>
      <p class="pt-info-small">Dudas sobre privacidad: <a href="mailto:${CONTACT_EMAIL}">${CONTACT_EMAIL}</a></p>`,
  },
};

const FEATURES: { ic: string; color: string; html: string }[] = [
  { ic: IC_SEARCH, color: "#f0c400", html: "<b>Camina, inspecciona y equípate</b> como un técnico de prevención." },
  { ic: IC_CLIP, color: "#2f8f57", html: "<b>Aprendes practicando</b> con minijuegos, pistas y decisiones reales." },
  { ic: IC_HAZ, color: "#c4122e", html: "<b>10 sectores</b> con riesgos reales de PRL · 6 casos de la historia + 4 extra." },
];

export class Portada {
  private root: HTMLElement;

  constructor(private onPlay: () => void) {
    injectPortadaStyles();
    this.root = document.createElement("div");
    this.root.id = "portada";
    document.body.appendChild(this.root);
    this.build();
  }

  private hasProgress(): boolean {
    const st = new GameState();
    return DISTRICTS.some((d) => d.sceneKey && st.chapter(d.id).done);
  }

  private build(): void {
    const progress = this.hasProgress();
    const primaryLabel = progress ? "Continuar aventura" : "Empezar aventura";

    this.root.innerHTML = `
      <div class="pt-tape"></div>
      <div class="pt-wrap">
        <header class="pt-head">
          <div class="pt-logo">
            <span class="pt-logo-mark">${HELMET}</span>
            <span class="pt-logo-txt"><b>LAS AVENTURAS DE EPI</b><small>Escape room de prevención de riesgos</small></span>
          </div>
          <span class="pt-eco-note">Un juego del ecosistema <b>Mi Ruta</b></span>
        </header>
        <div class="pt-rule"></div>

        <div class="pt-body">
          <div class="pt-left">
            <h1 class="pt-title"><span>Observa.</span><span>Equípate.</span><span class="pt-accent">Actúa.</span></h1>
            <div class="pt-underline"></div>
            <p class="pt-desc">Recorre <b>Villa Segura</b> como inspector. Explora, <b>detecta los riesgos</b> y elige la <b>medida correcta</b>. Cada decisión cuenta para mantener a todos a salvo.</p>
            <div class="pt-cta">
              <div class="pt-cta-row">
                <button class="pt-btn pt-btn-main" id="pt-play">${IC_PLAY}<span>${primaryLabel}</span></button>
                <button class="pt-btn pt-btn-ghost" id="pt-how">${IC_BOOK}<span>Cómo se juega</span></button>
              </div>
              <div class="pt-cta-links">
                <button class="pt-reset" id="pt-progress">Guardar / continuar en otro equipo</button>
                ${progress ? `<button class="pt-reset" id="pt-reset">Empezar de cero</button>` : ""}
              </div>
            </div>
            <div class="pt-features">
              ${FEATURES.map((f) => `<div class="pt-feat"><span class="pt-hex" style="--c:${f.color}"><span class="pt-hex-in">${f.ic}</span></span><span class="pt-ft">${f.html}</span></div>`).join("")}
            </div>
          </div>
          <div class="pt-scene"><div class="pt-scene-frame">${heroArt()}</div></div>
        </div>

        <div class="pt-eco">
          <span class="pt-eco-tag">${IC_LEAF}ECOSISTEMA MI RUTA</span>
          <span class="pt-eco-item">${IC_GRAD}Orientación y competencias para el aula</span>
          <span class="pt-eco-item">${IC_SHIELD}Prevención de riesgos laborales · FP</span>
          <span class="pt-lema">No preguntes qué harías. <b>Hazlo.</b></span>
        </div>
        <div class="pt-legal">
          <button class="pt-legal-lnk" data-info="contacto">Contacto</button>
          <button class="pt-legal-lnk" data-info="aviso">Aviso legal</button>
          <button class="pt-legal-lnk" data-info="privacidad">Privacidad</button>
          <span class="pt-legal-copy">© ${YEAR} Las Aventuras de EPI · Mi Ruta</span>
        </div>
      </div>

      <div class="pt-modal" id="pt-modal" hidden>
        <div class="pt-modal-card">
          <div class="pt-modal-head"><span class="pt-modal-tag"></span>CÓMO SE JUEGA</div>
          <div class="pt-modal-body">
            <ul class="pt-ul">
              <li><b>Muévete</b> con WASD o las flechas. Entra en un sector con <b>E</b> cuando aparezca el aviso.</li>
              <li>En cada sitio: <b>observa</b>, <b>habla</b> con la gente (E) e <b>inspecciona</b> lo que no encaje.</li>
              <li>Ante un riesgo: <b>1)</b> identifícalo, <b>2)</b> elige la medida por la <b>jerarquía</b> (eliminar &gt; colectiva &gt; organizativa &gt; EPI) y <b>3)</b> aplícala paso a paso.</li>
              <li><b>Equípate</b> en la taquilla según el trabajo y el entorno: ni de más ni de menos.</li>
              <li>La <b>seguridad</b> sube cuando actúas bien. Asegura un sector para desbloquear el siguiente.</li>
            </ul>
          </div>
          <div class="pt-modal-foot"><button class="pt-btn pt-btn-main" id="pt-modal-ok"><span>Entendido</span></button></div>
        </div>
      </div>`;

    const modal = this.root.querySelector<HTMLElement>("#pt-modal")!;
    this.root.querySelector("#pt-play")!.addEventListener("click", () => this.play());
    this.root.querySelector("#pt-how")!.addEventListener("click", () => { modal.hidden = false; });
    this.root.querySelector("#pt-modal-ok")!.addEventListener("click", () => { modal.hidden = true; });
    modal.addEventListener("click", (e) => { if (e.target === modal) modal.hidden = true; });
    this.root.querySelector("#pt-reset")?.addEventListener("click", () => {
      if (confirm("¿Empezar de cero? Se borrará tu progreso en Villa Segura.")) {
        SaveManager.reset();
        location.reload();
      }
    });
    this.root.querySelector("#pt-progress")?.addEventListener("click", () => openProgressModal());
    this.root.querySelectorAll<HTMLButtonElement>(".pt-legal-lnk").forEach((b) => {
      b.addEventListener("click", () => { const k = b.dataset.info; if (k && INFO[k]) this.openInfo(INFO[k]); });
    });
    this.tryLogo();
  }

  /** Abre una ficha informativa (contacto / aviso legal / privacidad) como modal. */
  private openInfo(info: { title: string; color: string; html: string }): void {
    const back = document.createElement("div");
    back.className = "pt-modal";
    back.innerHTML =
      `<div class="pt-modal-card"><div class="pt-modal-head" style="background:${info.color}"><span class="pt-modal-tag"></span>${info.title}</div>` +
      `<div class="pt-modal-body pt-info">${info.html}</div>` +
      `<div class="pt-modal-foot"><button class="pt-btn pt-btn-main"><span>Cerrar</span></button></div></div>`;
    this.root.appendChild(back);
    const close = (): void => back.remove();
    back.querySelector("button")!.addEventListener("click", close);
    back.addEventListener("click", (e) => { if (e.target === back) close(); });
  }

  /**
   * Si existe un logotipo real en `public/logo-epi.png`, sustituye la placa+texto
   * de la cabecera por la imagen. Si no está, se queda el respaldo tipográfico.
   */
  private tryLogo(): void {
    const url = `${import.meta.env.BASE_URL}logo-epi.png`;
    const img = new Image();
    img.onload = () => {
      const box = this.root.querySelector<HTMLElement>(".pt-logo");
      if (box) {
        box.classList.add("pt-logo--img");
        box.innerHTML = `<img class="pt-logo-img" src="${url}" alt="Las Aventuras de EPI · Escape room de prevención de riesgos">`;
      }
    };
    img.src = url;
  }

  private play(): void {
    this.root.classList.add("pt-gone");
    this.onPlay();
    window.setTimeout(() => this.root.remove(), 460);
  }
}

function injectPortadaStyles(): void {
  if (document.getElementById("pt-styles")) return;
  const tape = "repeating-linear-gradient(45deg,#f0c400 0 16px,#171308 16px 32px)";
  const bolts = (c: string): string =>
    `radial-gradient(circle at 7px 7px,${c} 1.6px,transparent 2.2px),` +
    `radial-gradient(circle at calc(100% - 7px) 7px,${c} 1.6px,transparent 2.2px),` +
    `radial-gradient(circle at 7px calc(100% - 7px),${c} 1.6px,transparent 2.2px),` +
    `radial-gradient(circle at calc(100% - 7px) calc(100% - 7px),${c} 1.6px,transparent 2.2px)`;
  const css = `
  #portada{position:fixed;inset:0;z-index:60;color:#e7dcc4;font-family:"Segoe UI",system-ui,sans-serif;overflow:auto;opacity:1;transition:opacity .42s ease;
    background:radial-gradient(120% 85% at 50% -12%,#2a2013,#14120c 68%),repeating-linear-gradient(45deg,rgba(255,255,255,.012) 0 2px,transparent 2px 7px),#14120c}
  #portada.pt-gone{opacity:0;pointer-events:none}
  .pt-tape{height:13px;background:${tape};opacity:.92;box-shadow:0 2px 6px rgba(0,0,0,.5)}
  .pt-wrap{max-width:1440px;width:93%;margin:0 auto;min-height:calc(100% - 13px);display:flex;flex-direction:column;padding-bottom:8px;position:relative}
  .pt-wrap::before,.pt-wrap::after{content:"";position:absolute;top:96px;bottom:56px;width:8px;background:repeating-linear-gradient(#3a2f1c 0 3px,transparent 3px 22px);opacity:.5}
  .pt-wrap::before{left:-22px}.pt-wrap::after{right:-22px}
  /* cabecera */
  .pt-head{display:flex;align-items:center;justify-content:space-between;gap:18px;padding:18px 0 12px}
  .pt-logo{display:flex;align-items:center;gap:16px}
  .pt-logo-mark{width:70px;height:70px;flex:0 0 auto;display:flex;align-items:center;justify-content:center;border:2px solid #4a3f24;border-radius:12px;
    background:${bolts("#6b5a2e")},linear-gradient(#221c12,#14100a);box-shadow:inset 0 0 0 2px #0c0a06,inset 0 2px 8px rgba(0,0,0,.6),0 3px 10px rgba(0,0,0,.5)}
  .pt-logo-mark svg{width:40px;height:40px;filter:drop-shadow(0 1px 1px rgba(0,0,0,.5))}
  .pt-logo-txt{display:flex;flex-direction:column;line-height:1.0}
  .pt-logo-txt b{font-size:clamp(27px,2.6vw,38px);font-weight:800;letter-spacing:.04em;color:#f4ead0;text-shadow:0 2px 0 rgba(0,0,0,.35)}
  .pt-logo-txt small{font-size:13px;text-transform:uppercase;letter-spacing:.16em;color:#f0c400;font-weight:700;margin-top:6px}
  .pt-logo--img{display:block}
  .pt-logo-img{height:clamp(78px,9vw,124px);width:auto;max-width:min(560px,54vw);display:block;filter:drop-shadow(0 4px 8px rgba(0,0,0,.55))}
  .pt-eco-note{font-size:14px;color:#9c8b6c}.pt-eco-note b{color:#f0c400}
  .pt-rule{height:2px;background:linear-gradient(90deg,#3a2f1c,#5a4a28,#3a2f1c)}
  /* cuerpo */
  .pt-body{flex:1 1 auto;display:grid;grid-template-columns:42% 58%;gap:38px;align-items:center;padding:16px 0}
  .pt-title{font-size:clamp(48px,6.2vw,94px);font-weight:800;text-transform:uppercase;letter-spacing:-.02em;line-height:.9;margin:0;color:#f4ead0;text-shadow:0 3px 0 rgba(0,0,0,.35)}
  .pt-title span{display:block}
  .pt-title .pt-accent{color:#f0c400;text-shadow:0 3px 0 rgba(0,0,0,.4)}
  .pt-underline{width:230px;max-width:66%;height:13px;margin:20px 0 22px;background:${tape};box-shadow:2px 2px 0 #000}
  .pt-desc{font-size:18px;line-height:1.55;color:#cbbb98;max-width:47ch;margin:0 0 26px}
  .pt-desc b{color:#f4ead0;font-weight:700}
  .pt-cta{display:flex;flex-direction:column;gap:12px;align-items:flex-start;margin-bottom:28px}
  .pt-cta-row{display:flex;gap:13px;flex-wrap:wrap}
  .pt-btn{display:inline-flex;align-items:center;gap:9px;border:2px solid #0c0a06;border-radius:9px;padding:.8em 1.4em;font-family:inherit;font-weight:800;font-size:15px;letter-spacing:.03em;cursor:pointer;text-transform:uppercase;transition:transform .09s,box-shadow .12s,filter .12s}
  .pt-btn:hover{transform:translateY(-2px)}
  .pt-btn-main{background:linear-gradient(#f6d21e,#e0b400);color:#171308;box-shadow:inset 0 0 0 2px rgba(255,255,255,.28),inset 0 -3px 0 rgba(120,80,0,.35),3px 4px 0 #000}
  .pt-btn-main:hover{filter:brightness(1.05);box-shadow:inset 0 0 0 2px rgba(255,255,255,.35),inset 0 -3px 0 rgba(120,80,0,.35),4px 6px 0 #000}
  .pt-btn-ghost{background:linear-gradient(#241d13,#191309);color:#f0e4c8;border-color:#4a3f24;box-shadow:inset 0 0 0 2px rgba(240,196,0,.12),3px 4px 0 #000}
  .pt-btn-ghost:hover{background:linear-gradient(#2b2216,#20180c)}
  .pt-cta-links{display:flex;gap:18px;flex-wrap:wrap;align-items:center}
  .pt-reset{background:none;border:none;color:#9c8b6c;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;text-decoration:underline;text-underline-offset:3px;padding:2px}
  .pt-reset:hover{color:#f0a08f}
  /* tarjetas de features con icono hexagonal */
  .pt-features{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
  .pt-feat{display:flex;align-items:flex-start;gap:11px;background:linear-gradient(#1e1810,#171208);border:1px solid #3a2f1c;border-radius:10px;padding:12px 12px;box-shadow:inset 0 1px 0 rgba(255,255,255,.03)}
  .pt-hex{width:38px;height:42px;flex:0 0 auto;display:flex;align-items:center;justify-content:center;background:var(--c);clip-path:polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%);box-shadow:0 2px 4px rgba(0,0,0,.4)}
  .pt-hex-in{display:flex;align-items:center;justify-content:center}
  .pt-ft{font-size:12.5px;line-height:1.35;color:#9c8b6c;padding-top:1px}.pt-ft b{color:#f0e4c8;font-weight:700}
  /* escena hero enmarcada */
  .pt-scene{align-self:stretch;display:flex;align-items:center;justify-content:center;min-width:0}
  .pt-scene-frame{position:relative;width:100%;border:3px solid #4a3f24;border-radius:12px;padding:8px;background:${bolts("#6b5a2e")},linear-gradient(#221c12,#14100a);box-shadow:inset 0 0 0 2px #0c0a06,0 12px 34px rgba(0,0,0,.5)}
  .pt-scene-frame::before,.pt-scene-frame::after{content:"";position:absolute;width:18px;height:18px;border:3px solid #f0c400;opacity:.65}
  .pt-scene-frame::before{top:-3px;left:-3px;border-right:none;border-bottom:none;border-top-left-radius:10px}
  .pt-scene-frame::after{bottom:-3px;right:-3px;border-left:none;border-top:none;border-bottom-right-radius:10px}
  .pt-scene svg{width:100%;height:auto;max-height:74vh;display:block;border-radius:7px}
  /* franja ecosistema */
  .pt-eco{border-top:2px solid #3a2f1c;margin-top:6px;padding:14px 0 8px;display:flex;align-items:center;gap:0;flex-wrap:wrap}
  .pt-eco-tag{display:inline-flex;align-items:center;gap:7px;font-weight:800;text-transform:uppercase;font-size:11px;letter-spacing:.08em;color:#171308;background:#f0c400;padding:8px 22px 8px 14px;margin-right:22px;clip-path:polygon(0 0,calc(100% - 11px) 0,100% 50%,calc(100% - 11px) 100%,0 100%)}
  .pt-eco-item{display:inline-flex;align-items:center;gap:8px;font-size:13.5px;color:#9c8b6c;padding:0 22px;position:relative}
  .pt-eco-item+.pt-eco-item::before{content:"";position:absolute;left:0;top:50%;transform:translateY(-50%);width:1.5px;height:20px;background:#3a2f1c}
  .pt-lema{margin-left:auto;font-size:14px;color:#9c8b6c;font-style:italic}.pt-lema b{color:#f0c400;font-style:normal}
  /* pie legal */
  .pt-legal{display:flex;align-items:center;gap:16px;flex-wrap:wrap;padding:8px 0 4px}
  .pt-legal-lnk{background:none;border:none;color:#9c8b6c;font-family:inherit;font-size:12.5px;font-weight:600;cursor:pointer;padding:0}
  .pt-legal-lnk:hover{color:#f0c400;text-decoration:underline;text-underline-offset:3px}
  .pt-legal-copy{margin-left:auto;font-size:12px;color:#6a5c44}
  .pt-info p{margin:.35rem 0 .7rem;line-height:1.55}
  .pt-info b{color:#171308}
  .pt-info a{color:#185fa5;font-weight:700}
  .pt-info-mail{font-size:1.05rem}
  .pt-info-small{font-size:.85rem;color:#5a4a2c;border-top:1px solid #cbb891;padding-top:.6rem;margin-top:.4rem}
  /* modal */
  .pt-modal{position:fixed;inset:0;z-index:70;background:rgba(10,8,4,.72);display:flex;align-items:center;justify-content:center;padding:18px}
  .pt-modal[hidden]{display:none}
  .pt-modal-card{background:#efe3cb;border:2px solid #241d14;border-radius:6px;max-width:520px;width:100%;max-height:86vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 22px 56px rgba(0,0,0,.6)}
  .pt-modal-head{background:#185fa5;color:#fff;font-weight:800;font-size:1rem;letter-spacing:.05em;padding:.72rem 1rem;display:flex;align-items:center;gap:.5rem}
  .pt-modal-tag{width:14px;height:14px;background:${tape};box-shadow:0 0 0 1px rgba(0,0,0,.3)}
  .pt-modal-body{padding:1rem;overflow:auto;color:#241d14}
  .pt-ul{margin:0;padding-left:1.1rem;display:flex;flex-direction:column;gap:.5rem;line-height:1.5}
  .pt-modal-foot{padding:.7rem 1rem;background:#e6d7ba;display:flex;justify-content:flex-end;border-top:1px solid #cbb891}
  .pt-modal-foot .pt-btn{color:#171308}
  @media(max-width:1080px){.pt-body{grid-template-columns:1fr;gap:16px}.pt-scene{order:-1}.pt-scene svg{max-height:36vh}.pt-title{font-size:clamp(40px,10vw,64px)}.pt-desc{font-size:16px}.pt-wrap::before,.pt-wrap::after{display:none}}
  @media(max-width:620px){.pt-wrap{width:90%}.pt-eco-note,.pt-lema{display:none}.pt-cta-row{width:100%}.pt-btn{flex:1;justify-content:center}.pt-features{grid-template-columns:1fr}}
  `;
  const style = document.createElement("style");
  style.id = "pt-styles";
  style.textContent = css;
  document.head.appendChild(style);
}
